import type { IPublicClientApplication } from "@azure/msal-browser";
import { useMsal } from "@azure/msal-react";
import { useEffect, useMemo, useState } from "react";

import { getActiveB2CAccount } from "./b2c-utils.ts";
import { clearAnalyticsDataLayer } from "./google-analytics-helper.ts";
import { clearMonitoringUser } from "./monitoring.ts";

const handleLogout = (instance: IPublicClientApplication) => {
	clearAnalyticsDataLayer();
	clearMonitoringUser();
	// Resolve the account BEFORE logging out and pass it to `logoutRedirect()`.
	// This does NOT add an `id_token_hint`/`logout_hint` to the B2C end-session
	// request — MSAL only sends those when the request sets `idTokenHint`/
	// `logoutHint` explicitly, which we don't here. Passing `account` instead
	// scopes MSAL's own cache clearing to that (possibly federated) account so
	// a second, wrong-realm account isn't left behind for the next login to
	// pick up. B2C itself still ends the server-side session via its own
	// session cookie, independent of this parameter.
	const account =
		instance.getActiveAccount() ?? instance.getAllAccounts()[0] ?? undefined;

	// IMPORTANT: do NOT call `sessionStorage.clear()` here. Wiping the cache
	// before `logoutRedirect()` removes the account/interaction state MSAL needs
	// to build a valid end-session request, leaving the B2C session alive.
	// `logoutRedirect()` clears MSAL's own cache and navigates the browser to the
	// B2C sign-out endpoint, so no manual storage clear is required.
	void instance.logoutRedirect({ account });
};

export function useIsAuthenticated() {
	const { accounts, instance } = useMsal();
	const account = getActiveB2CAccount(instance) ?? undefined;
	const [isAuthenticated, setIsAuthenticated] = useState(false);

	useEffect(() => {
		setIsAuthenticated(accounts.length > 0 && account !== undefined);
	}, [account, accounts]);

	// Get full name from account or fallback to username or default "User"
	const firstName = account?.name?.split(" ")[0] || "";
	const lastName = account?.name?.split(" ").slice(1).join(" ") || "";
	const fullName = account?.name || account?.username || "User";
	//The current logic gets the employeeId from the "employeeid" claim, but if that claim does not exist (as is the case for external users) it needs to use the com.tql.id claim instead.
	const employeeId = (account?.idTokenClaims?.["employeeid"] as string) || "";
	const email =
		((account?.username || account?.idTokenClaims?.["email"]) as string) || "";

	// Pull the user guid off the claim. This claim only appears for external users.
	const userGuid = (account?.idTokenClaims?.["com.tql.id"] as string) || "";

	// Get customer IDs from B2C token claim - keep as strings for URL query params
	const customerIds: string[] = useMemo(
		() =>
			(account?.idTokenClaims?.["com.tql.customer"] as string[] | undefined) ??
			[],
		[account],
	);
	const accountRoles = account?.idTokenClaims?.roles ?? [];

	return {
		accounts,
		isAuthenticated,
		username: fullName,
		firstName,
		lastName,
		fullName,
		employeeId,
		email,
		userGuid,
		customerIds,
		accountRoles,
		handleLogout: () => handleLogout(instance),
	};
}
